import { useMemo, useState } from "react";

import { PROFISSIONAIS_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  TABLE_SETTINGS_APPLIED_MESSAGE,
  useLastKnownTotal,
  useTableSettings,
  useTableState,
  useToast,
} from "@/ds";
import type { FilterDefinition, FilterValue, MonthRangeValue } from "@/ds";
import { validatePeriodo } from "@/modules/carga-trabalho";
import { createExportFeedback, describeReportFilters, useFileExport } from "@/modules/export";
import {
  currentMonthRange,
  DEFAULT_PROFISSIONAIS_SORT,
  EMPTY_PROFISSIONAIS_FILTERS,
  exportProfissionais,
  hasProfissionaisFilters,
  INDISPONIVEL_FILTER_OPTIONS,
  isProfissionaisSortField,
  monthRangeToValues,
  PROFISSIONAIS_FILTER_COLUMNS,
  PROFISSIONAIS_FILTER_LABELS,
} from "@/modules/recursos";
import type { ProfissionaisFilters } from "@/modules/recursos";

import { useProfissionais } from "../hooks/use-profissionais";
import { useProfissionaisFiltrosSalvos } from "./hooks/use-profissionais-filtros-salvos";
import { RECURSOS_MOBILE_QUERY } from "../recursos.constants";
import { useProfissionalFilterOptions } from "./hooks/use-profissional-filter-options";
import {
  PROFISSIONAIS_COLUMN_KEYS,
  PROFISSIONAIS_EXPORT_FILE_NAME,
} from "./profissionais.constants";
import { formatPeriodoLabel, toProfissionalLinha } from "./profissionais.utils";
import type {
  ExportToastMessage,
  ProfissionaisColumnKey,
  ProfissionaisScreenViewModel,
} from "./types-profissionais";

/**
 * Listagem de Profissionais (HU 1761) — consulta somente leitura: busca, período Mês/Ano (padrão: o mês corrente,
 * comentário de 24/09), filtros Cargo, Time e Indisponível, paginação e ordenação no servidor.
 */
export function useProfissionaisScreen(): ProfissionaisScreenViewModel {
  const toast = useToast();
  const isMobile = useMediaQuery(RECURSOS_MOBILE_QUERY);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<ProfissionaisFilters>(EMPTY_PROFISSIONAIS_FILTERS);
  const [periodo, setPeriodo] = useState<MonthRangeValue>(() => currentMonthRange());
  const settings = useTableSettings<ProfissionaisColumnKey>({
    densityKey: PROFISSIONAIS_TABLE_DENSITY_KEY,
    allColumns: PROFISSIONAIS_COLUMN_KEYS,
    mainColumns: PROFISSIONAIS_COLUMN_KEYS,
  });
  // Erro e carregamento zeram o total da lista: a página só volta à 1ª com um total real menor.
  const { lastTotal, remember } = useLastKnownTotal();
  const table = useTableState({
    isSortField: isProfissionaisSortField,
    total: lastTotal,
  });

  const cargo = useProfissionalFilterOptions("cargo");
  const time = useProfissionalFilterOptions("time");

  // Cada lista de opções tem o seu serviço: uma que falha não derruba a outra. Indisponível tem Sim/Não fixos.
  const filterColumns = useMemo<FilterDefinition[]>(() => {
    const remotas = { cargo, time };

    return PROFISSIONAIS_FILTER_COLUMNS.map((key) => {
      const label = PROFISSIONAIS_FILTER_LABELS[key];
      const searchPlaceholder = `Buscar ${label.toLowerCase()}`;

      return key === "indisponivel"
        ? {
            key,
            label,
            options: INDISPONIVEL_FILTER_OPTIONS,
            type: "select" as const,
            searchPlaceholder,
          }
        : {
            key,
            label,
            options: remotas[key].options,
            isLoading: remotas[key].isLoading,
            hasError: remotas[key].hasError,
            type: "multiselect" as const,
            searchPlaceholder,
          };
    });
  }, [cargo, time]);

  const periodoConsultado = monthRangeToValues(periodo);
  const sort = table.sort ?? DEFAULT_PROFISSIONAIS_SORT;

  const lista = useProfissionais({
    search,
    filters,
    periodo: periodoConsultado,
    page: table.page + 1,
    pageSize: table.rows,
    sort,
  });
  remember(lista.status, lista.total);

  const linhas = useMemo(() => lista.profissionais.map(toProfissionalLinha), [lista.profissionais]);

  // O feedback de exportação fala com o `ref` do Prime; este adaptador o liga ao `useToast` do DS.
  const toastRef = useMemo(
    () => ({
      current: {
        show: ({ severity, summary, detail, life }: ExportToastMessage) =>
          toast.show({ severity, summary, detail, life }),
      },
    }),
    [toast],
  );

  /** Exportar Visualização (HU 2389): busca, filtros, período e ordenação em vigor, todas as páginas. */
  const exportView = useFileExport({
    fileName: PROFISSIONAIS_EXPORT_FILE_NAME,
    loadFile: (format) =>
      exportProfissionais({
        busca: search,
        filters,
        filtros: describeReportFilters(search, filterColumns, filters, {
          periodo: formatPeriodoLabel(periodo),
        }),
        periodo: periodoConsultado,
        sort,
        format,
      }),
    isEmpty: () => lista.status === "success" && lista.total === 0,
    ...createExportFeedback(toastRef),
  });

  const hasSearch = search.trim() !== "";

  function buscar(texto: string) {
    setSearch(texto);
    table.resetPage();
  }

  function aplicarFiltros(value: FilterValue) {
    setFilters(
      PROFISSIONAIS_FILTER_COLUMNS.reduce<ProfissionaisFilters>(
        (next, column) => ({ ...next, [column]: value[column] ?? [] }),
        { ...EMPTY_PROFISSIONAIS_FILTERS },
      ),
    );
    table.resetPage();
  }

  const filtrosSalvos = useProfissionaisFiltrosSalvos(aplicarFiltros);

  /** O período é obrigatório: um intervalo recusado (mais de 36 meses) avisa e mantém o anterior. */
  function escolherPeriodo(value: MonthRangeValue) {
    const { from, to } = monthRangeToValues(value);
    const erro = validatePeriodo(from, to);
    if (erro) {
      toast.warn(erro);
      return;
    }

    setPeriodo(value);
    table.resetPage();
  }

  function limparCriterios() {
    if (hasSearch) setSearch("");
    else setFilters(EMPTY_PROFISSIONAIS_FILTERS);
    table.resetPage();
  }

  function avisarConfiguracoesAplicadas() {
    toast.success("Sucesso", TABLE_SETTINGS_APPLIED_MESSAGE);
  }

  return {
    isMobile,
    search,
    buscar,
    periodo,
    escolherPeriodo,
    filterColumns,
    filterValue: filters,
    aplicarFiltros,
    filtrosSalvos,
    linhas,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    table,
    settings,
    hasSearch,
    hasFilters: hasProfissionaisFilters(filters),
    limparCriterios,
    tentarNovamente: lista.reload,
    exportView,
    avisarConfiguracoesAplicadas,
  };
}
